// Bird entity manager - handles creation, animation, and lifecycle of birds
import * as THREE from 'three';

// Pre-allocated temporary objects to avoid GC pressure during bird recycling
const _tempDirection = new THREE.Vector3();
const _tempQuaternion = new THREE.Quaternion();
const _tempUp = new THREE.Vector3(0, 1, 0);
const _tempForward = new THREE.Vector3();
const _tempMatrix = new THREE.Matrix4();
const _tempOrigin = new THREE.Vector3(0, 0, 0);

export class BirdManager {
    constructor(scene, camera, worldTranslator, config = {}, preloader = null) {
        this.scene = scene;
        this.camera = camera;
        this.worldTranslator = worldTranslator;

        // Self-register for preload tracking with dispose callback
        this._onPreloadComplete = preloader?.register('birds', {
            dispose: () => this.dispose()
        });

        // Configuration
        this.enabled = config.enabled !== undefined ? config.enabled : true;
        this.count = config.count || 5;
        this.size = config.size || 22;
        this.minHeight = config.minHeight || 500;
        this.maxHeight = config.maxHeight || 2000;
        this.minSpeed = config.minSpeed || 60;
        this.maxSpeed = config.maxSpeed || 300;
        this.fadeThreshold = config.fadeThreshold || 0.25;
        this.fogFar = config.fogFar || 5000;

        // Active birds
        this.birds = [];

        // Disposal state
        this._disposed = false;
    }

    createBird(zPosition) {
        // Create bird container (parent object for both wings)
        const bird = new THREE.Group();

        // Left wing
        const leftWingGeometry = new THREE.BufferGeometry();
        const leftWingVertices = new Float32Array([
            0, 0, 0,                    // Base (center)
            -this.size, 0, 0,       // Wing tip (left)
            -this.size * 0.3, 0, this.size * 0.5  // Wing back edge
        ]);
        leftWingGeometry.setAttribute('position', new THREE.BufferAttribute(leftWingVertices, 3));

        const material = new THREE.MeshBasicMaterial({
            color: 0x000000,
            side: THREE.DoubleSide,
            fog: true
        });

        const leftWing = new THREE.Mesh(leftWingGeometry, material);
        bird.add(leftWing);

        // Right wing
        const rightWingGeometry = new THREE.BufferGeometry();
        const rightWingVertices = new Float32Array([
            0, 0, 0,                    // Base (center)
            this.size, 0, 0,        // Wing tip (right)
            this.size * 0.3, 0, this.size * 0.5  // Wing back edge
        ]);
        rightWingGeometry.setAttribute('position', new THREE.BufferAttribute(rightWingVertices, 3));

        const rightWing = new THREE.Mesh(rightWingGeometry, material.clone());
        bird.add(rightWing);

        // Store wing references for animation
        bird.userData.leftWing = leftWing;
        bird.userData.rightWing = rightWing;

        // Random position in world space
        const x = (Math.random() - 0.5) * 4000; // Random X position
        const y = this.minHeight + Math.random() * (this.maxHeight - this.minHeight);
        const z = zPosition;

        bird.position.set(x, y, z);

        // Flight direction: mostly toward camera with lateral movement
        const directionX = (Math.random() - 0.5) * 1.5; // Side to side movement
        const directionY = (Math.random() - 0.5) * 0.3; // Slight up/down
        const directionZ = 0.3 + Math.random() * 0.7; // Mostly toward camera (positive Z = back toward camera)

        // Use pre-allocated direction vector, then clone for storage
        _tempDirection.set(directionX, directionY, directionZ).normalize();

        // Random speed
        const speed = this.minSpeed + Math.random() * (this.maxSpeed - this.minSpeed);

        // Store movement data (clone direction since we need to keep it per-bird)
        bird.userData.direction = _tempDirection.clone();
        bird.userData.speed = speed;
        bird.userData.flapOffset = Math.random() * Math.PI * 2; // Random flap phase

        // Orient bird to face its direction of travel using pre-allocated objects
        _tempForward.copy(_tempDirection);
        _tempMatrix.lookAt(_tempOrigin, _tempForward, _tempUp);
        _tempQuaternion.setFromRotationMatrix(_tempMatrix);
        bird.quaternion.copy(_tempQuaternion);

        this.worldTranslator.add(bird);
        return bird;
    }

    initialize() {
        if (!this.enabled) {
            this._onPreloadComplete?.();
            return;
        }

        // Create birds distributed ahead of the player (in local worldGroup coordinates)
        // Negative Z = ahead, positive Z = behind camera
        const spawnRangeStart = 0;
        const spawnRangeEnd = -this.fogFar - 1000;


        for (let i = 0; i < this.count; i++) {
            const zPos = spawnRangeStart + (spawnRangeEnd - spawnRangeStart) * (i / this.count);
            const bird = this.createBird(zPos);
            this.birds.push(bird);
        }

        this._onPreloadComplete?.();
    }

    update(deltaTime, ambientLightIntensity) {
        if (this._disposed || !this.enabled || this.birds.length === 0) return;

        const recycleDistance = 200;
        const spawnDistance = this.fogFar + 1000;

        // Calculate day/night visibility - simplified clamp (Phase 2.2 optimization)
        const fadeStart = this.fadeThreshold - 0.1;
        const birdVisibility = Math.max(0, Math.min(1, (ambientLightIntensity - fadeStart) * 10));

        // Update and recycle birds
        for (let i = this.birds.length - 1; i >= 0; i--) {
            const bird = this.birds[i];

            // Apply visibility
            bird.visible = birdVisibility > 0.01;

            // Move bird
            bird.position.x += bird.userData.direction.x * bird.userData.speed * deltaTime;
            bird.position.y += bird.userData.direction.y * bird.userData.speed * deltaTime;
            bird.position.z += bird.userData.direction.z * bird.userData.speed * deltaTime;

            // Wing flap animation - rotate wings up and down
            const flapAngle = Math.sin(performance.now() * 0.01 + bird.userData.flapOffset) * 0.5;
            bird.userData.leftWing.rotation.z = -flapAngle;
            bird.userData.rightWing.rotation.z = flapAngle;

            // Check if bird is behind camera (in local worldGroup coordinates)
            // Positive local Z = behind camera
            const isBehindCamera = this.worldTranslator.isPastCamera(bird.position.z, recycleDistance);
            const isTooFar = Math.abs(bird.position.x) > 10000 || Math.abs(bird.position.y) > 10000;

            if (isBehindCamera || isTooFar) {
                // Remove from worldGroup and dispose
                this.worldTranslator.remove(bird);

                // Dispose of each wing's geometry and material
                bird.children.forEach(wing => {
                    if (wing.geometry) wing.geometry.dispose();
                    if (wing.material) wing.material.dispose();
                });

                // Remove from array
                this.birds.splice(i, 1);

                // Create new bird ahead (negative Z in local coordinates)
                const newZPos = -spawnDistance - Math.random() * 1000;
                const newBird = this.createBird(newZPos);
                this.birds.push(newBird);
            }
        }
    }

    dispose() {
        if (this._disposed) return;
        this._disposed = true;

        // Clean up all birds
        this.birds.forEach(bird => {
            this.worldTranslator.remove(bird);
            bird.children.forEach(wing => {
                if (wing.geometry) wing.geometry.dispose();
                if (wing.material) wing.material.dispose();
            });
        });
        this.birds = [];
    }
}
